import Group from "@mui/icons-material/Group";
import { Delete, Star } from "@mui/icons-material";
import { Divider, IconButton, ListItemText, MenuItem, Popover, useMediaQuery } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { changes } from "src/components/utils/fields";
import { M_Fieldset, M_FormFavorites, M_Button, M_InputTextField, useFavorites } from "src/M_Components";
import { QuoteFormField } from "./QuoteFormField";
 
function GroupFavoriteActions({ groupName, t }) {
  const favCtx = useFavorites();
  const [anchorEl, setAnchorEl] = useState(null);
  const [name, setName] = useState('');
 
  if (!favCtx) return null;
 
  const { saveGroupFavorite, getGroupFavorites, handleApplyFavorite, handleDeleteFavorite } = favCtx;
  const groupFavs = getGroupFavorites(groupName);
 
  return (
    <div style={{ position: 'absolute', top: '10px', right: '18px', zIndex: 10 }}>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Star sx={{ color: groupFavs.length > 0 ? 'var(--color-yellow, #f9a825)' : '#ccc', fontSize: '20px' }} />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setName(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div style={{ minWidth: '250px' }}>
          <div style={{ padding: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <M_InputTextField
              size="small"
              label={t('FavoriteName') || 'Name'}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && name.trim()) {
                  saveGroupFavorite(groupName, name);
                  setName('');
                }
              }}
              autoFocus
              fullWidth
            />
            <M_Button
              normal
              hoverEffect
              label={t('Save') || 'Save'}
              backgroundColor={'var(--color-blue)'}
              labelColor="var(--color-white)"
              onClick={() => {
                if (name.trim()) {
                  saveGroupFavorite(groupName, name);
                  setName('');
                }
              }}
            />
          </div>
          {groupFavs.length > 0 && <Divider />}
          {groupFavs.map(fav => (
            <MenuItem
              key={fav.Id}
              onClick={() => { handleApplyFavorite(fav); setAnchorEl(null); }}
              sx={{
                '& .fav-delete': { opacity: 0, transition: 'opacity 0.2s' },
                '&:hover .fav-delete': { opacity: 1 }
              }}
            >
              <ListItemText>{fav.Description}</ListItemText>
              <IconButton
                className="fav-delete"
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDeleteFavorite(fav.Id, e); }}
                sx={{ marginLeft: '8px' }}
              >
                <Delete fontSize="small" sx={{ color: 'var(--color-red, #d32f2f)' }} />
              </IconButton>
            </MenuItem>
          ))}
        </div>
      </Popover>
    </div>
  );
}
 
export const QuoteEntities = ({ dataState, setDataState }) => {
  const { t } = useTranslation();
 
  const matches6 = useMediaQuery("(min-width:1380px)");
  const matches7 = useMediaQuery("(min-width:1000px)");
  const matches13 = useMediaQuery("(min-width:850px)");
 
  const formFields = [
    {
      display: t("Company"),
      required: true,
      code: "clientName",
      type: "foreignKey",
      entity: "Account",
      filterField: "AccountCode",
      labelFields: ["AccountCode"],
      fields: [
        { key: "AccountCode", value: "clientCode" },
        { key: "AccountName", value: "clientName" },
        { key: "AccountVATNumber", value: "clientVat" },
        { key: "Id", value: "userCompanyId" },
      ],
      favoritesFieldName: t("Company"),
      favoritesGroupName: "Entities",
      favoritesVarKey: "clientCode",
      favoritesIdKey: "userCompanyId",
    },
    { display: t("Client"), code: "clientName", type: "disabled", favoritesFieldName: t("Client"), favoritesGroupName: "Entities", favoritesVarKey: "clientName" },
    { display: t("ClientVAT"), code: "clientVat", type: "disabled", favoritesFieldName: t("ClientVAT"), favoritesGroupName: "Entities", favoritesVarKey: "clientVat" },
    {
      display: t("Shipper"),
      required: false,
      code: "shipperCode",
      type: "foreignKey",
      entity: "Account",
      filterField: "AccountCode",
      labelFields: ["AccountCode"],
      fields: [
        { key: "AccountCode", value: "shipperCode" },
        { key: "AccountVATNumber", value: "shipperVat" },
        { key: "Id", value: "shipperId" },
      ],
      favoritesFieldName: t("Shipper"),
      favoritesGroupName: "Entities",
      favoritesVarKey: "shipperCode",
      favoritesIdKey: "shipperId",
    },
    { display: t("ShipperVAT"), code: "shipperVat", type: "disabled", favoritesFieldName: t("ShipperVAT"), favoritesGroupName: "Entities", favoritesVarKey: "shipperVat" },
    {
      display: t("Consignee"),
      required: false,
      code: "consigneeCode",
      type: "foreignKey",
      entity: "Account",
      filterField: "AccountCode",
      labelFields: ["AccountCode"],
      fields: [
        { key: "AccountCode", value: "consigneeCode" },
        { key: "AccountVATNumber", value: "consigneeVat" },
        { key: "Id", value: "consigneeId" },
      ],
      favoritesFieldName: t("Consignee"),
      favoritesGroupName: "Entities",
      favoritesVarKey: "consigneeCode",
      favoritesIdKey: "consigneeId",
    },
    { display: t("ConsigneeVAT"), code: "consigneeVat", type: "disabled", favoritesFieldName: t("ConsigneeVAT"), favoritesGroupName: "Entities", favoritesVarKey: "consigneeVat" },
  ];
 
  const entitiesVarNameDict = useMemo(() => new Map([
    ['clientName',    dataState.clientName    || ''],
    ['userCompanyId', dataState.userCompanyId || ''],
    ['clientCode',    dataState.clientCode    || ''],
    ['clientVat',     dataState.clientVat     || ''],
    ['shipperCode',   dataState.shipperCode   || ''],
    ['shipperId',     dataState.shipperId     || ''],
    ['shipperVat',    dataState.shipperVat    || ''],
    ['consigneeCode', dataState.consigneeCode || ''],
    ['consigneeId',   dataState.consigneeId   || ''],
    ['consigneeVat',  dataState.consigneeVat  || ''],
  ]), [
    dataState.clientName, dataState.userCompanyId,
    dataState.clientCode, dataState.clientVat,
    dataState.shipperCode, dataState.shipperId, dataState.shipperVat,
    dataState.consigneeCode, dataState.consigneeId, dataState.consigneeVat,
  ]);
 
  const onChangeCompany = (row, fields, obj) => {
    if (obj) {
      changes("clientName", obj.AccountName, setDataState);
      changes("clientCode", obj.AccountCode || "", setDataState);
      changes("clientVat", obj.AccountVATNumber || "", setDataState);
      const matchingCompany = dataState.companyOptions?.find(c => c.value === obj.Id || c.AccountId === obj.Id);
      changes("userCompanyId", matchingCompany?.Id || obj.Id, setDataState);
    } else {
      changes("clientName", "", setDataState);
      changes("clientCode", "", setDataState);
      changes("clientVat", "", setDataState);
      changes("userCompanyId", "", setDataState);
    }
  };
 
  return (
    <M_FormFavorites
      entityName="Quotes"
      pageUrl="Quote"
      varNameDict={entitiesVarNameDict}
      setVarNameDict={() => {}}
      onFavoriteLoad={(loadedData) => {
        setDataState(prev => ({ ...prev, ...loadedData }));
      }}
      onAfterApply={() => {}}
    >
      <div style={{ position: 'relative' }}>
        <GroupFavoriteActions groupName="Entities" t={t} />
        <M_Fieldset
          labelInside
          label={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "25px",
                marginLeft: "10px",
              }}
            >
              <Group style={{ marginRight: "10px", scale: "1.3" }} />{" "}
              {t("Entities")}
            </div>
          }
          elements={
            <div
              style={{
                display: "flex",
                flexDirection: !matches6 ? "column" : "row",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: !matches7 ? "column" : "row",
                }}
              >
                {formFields
                  .filter(
                    (field) =>
                      field.code?.startsWith("company") ||
                      field.code?.startsWith("client"),
                  )
                  .map((field, index) => {
                    if (field.code === "clientName") {
                      return (
                        <>
                          <QuoteFormField
                            key={`QuoteEntities-${index}`}
                            fields={field}
                            index={index}
                            data={dataState}
                            setData={setDataState}
                            hideLabels={null}
                            returnFunc={onChangeCompany}
                            size={null}
                            options={dataState["companyOptions"]}
                            toggleable={dataState.isRevisionView ? false : dataState.isMultiEdit}
                            disabled={dataState.isRevisionView}
                          />
                        </>
                      );
                    }
                    return (
                      <>
                        <QuoteFormField
                          key={`QuoteEntities-${index}`}
                          fields={field}
                          index={index}
                          data={dataState}
                          setData={setDataState}
                          hideLabels={null}
                          returnFunc={null}
                          toggleable={dataState.isRevisionView ? false : dataState.isMultiEdit}
                          disabled={dataState.isRevisionView}
                        />
                      </>
                    );
                  })}
              </div>
 
              <div
                style={{
                  display: "flex",
                  flexDirection: !matches13 ? "column" : "row",
                  flexFlow: "wrap",
                }}
              >
                {formFields
                  .filter(
                    (field) =>
                      !field.code?.startsWith("company") &&
                      !field.code?.startsWith("client"),
                  )
                  .map((field, index) => {
                    return (
                      <>
                        <QuoteFormField
                          key={`QuoteEntities-${index}`}
                          fields={field}
                          index={index}
                          data={dataState}
                          setData={setDataState}
                          hideLabels={null}
                          returnFunc={null}
                          toggleable={dataState.isRevisionView ? false : dataState.isMultiEdit}
                          disabled={dataState.isRevisionView}
                        />
                      </>
                    );
                  })}
              </div>
            </div>
          }
        />
      </div>
    </M_FormFavorites>
  );
};